import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useCallback, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { LinearProgressWithLabel } from "../myExparience/createExparience/steps/ProgressLine";
import EmailIcon from "@mui/icons-material/Email";
import { useViewport } from "../../helpers";
import Lottie from "lottie-react";
import PacesLottie from "../../assets/paces.json";
import "./classManagementChartsOverides.css";

const ClassesManagementCharts = ({ myClass }) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const handleSort = useCallback(
    (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const filteredMembers = useMemo(() => {
    let members = myClass.members;

    if (searchQuery) {
      members = members.filter(
        (member) =>
          member.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
          member.lastname.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Inline sorting logic
    if (sortConfig.key) {
      return [...members].sort((a, b) => {
        if (sortConfig.key === "lastname") {
          return sortConfig.direction === "ascending"
            ? a.lastname.localeCompare(b.lastname)
            : b.lastname.localeCompare(a.lastname);
        }
        if (sortConfig.key === "firstname") {
          return sortConfig.direction === "ascending"
            ? a.firstname.localeCompare(b.firstname)
            : b.firstname.localeCompare(a.firstname);
        }
        return 0;
      });
    }

    return members;
  }, [myClass.members, searchQuery, sortConfig]);

  const Charts = useMemo(() => {
    return (
      <Box
        id="stats"
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          width: "100%",
          height: "100%",
        }}
      >
        <Box sx={{ width: "100%", marginBottom: "2rem" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("classManagement.statistics.searchStudent")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                mt: 1,
                mb: 1,
                width: width < 769 ? "95%" : "50%",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer component={Paper} sx={{ fontSize: "10px" }}>
            <Table
              aria-label="collapsible table"
              sx={{
                "& .MuiTableCell-root": {
                  fontSize: width < 769 ? "12px" : "inherit",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell />
                  <TableCell
                    sx={{
                      color: "#D3AF5F",
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                      maxWidth: width < 769 ? "80px" : "auto",
                      padding: width < 769 ? "16px 4px" : "16px",
                      "&:hover": {
                        backgroundColor: "rgba(211, 175, 95, 0.1)",
                      },
                    }}
                    onClick={() => handleSort("lastname")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "left",
                      }}
                    >
                      {t("classManagement.statistics.lastName")}
                      <Box
                        sx={{
                          width: 24,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {sortConfig.key === "lastname" &&
                          (sortConfig.direction === "ascending" ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ))}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#D3AF5F",
                      cursor: "pointer",
                      userSelect: "none",
                      maxWidth: width < 769 ? "80px" : "auto",
                      padding: width < 769 ? "16px 4px" : "16px",
                      "&:hover": {
                        backgroundColor: "rgba(211, 175, 95, 0.1)",
                      },
                    }}
                    onClick={() => handleSort("firstname")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "left",
                      }}
                    >
                      {t("classManagement.statistics.firstName")}
                      <Box
                        sx={{
                          width: 24,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {sortConfig.key === "firstname" &&
                          (sortConfig.direction === "ascending" ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ))}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#D3AF5F",
                      padding: width < 769 ? "8px 0" : "16px",
                    }}
                    align="center"
                  >
                    {t("classManagement.statistics.contact")}
                  </TableCell>
                  <TableCell sx={{ color: "#D3AF5F" }} align="center">
                    {t("classManagement.statistics.timeSpentTotal")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#D3AF5F",
                      padding: width < 769 ? "8px 0" : "16px",
                    }}
                    align="center"
                  >
                    {t("classManagement.statistics.paces")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t("classManagement.statistics.noResults")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMembers.map((member, index) => (
                    <Row
                      row={member}
                      key={
                        member.id || `${member.firstname}-${member.lastname}`
                      }
                      t={t}
                      width={width}
                      myClass={myClass}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }, [t, searchQuery, filteredMembers, sortConfig, handleSort, width, myClass]);

  if (!myClass) return null;
  return Charts;
};

export default ClassesManagementCharts;

const Row = React.memo(
  ({ row, t, width, myClass }) => {
    const [open, setOpen] = useState(false);

    const formatDate = (date) => {
      // Use Intl to format the date according to the browser language
      const browserLanguage = navigator.language || "en-US"; // Example of fallback
      return new Intl.DateTimeFormat(browserLanguage, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(date));
    };

    const formatTimeSpent = (milliseconds) => {
      const seconds = milliseconds / 1000;
      return formatTimeSpentInSeconds(seconds);
    };

    const formatTimeSpentInSeconds = (seconds) => {
      if (seconds < 60) {
        return `${seconds.toFixed(0)} s`; // Display only seconds
      }
      const minutes = Math.floor(seconds / 60); // Calculate minutes
      const remainingSeconds = Math.floor(seconds % 60); // Calculate remaining seconds
      return `${minutes} min ${remainingSeconds} s`; // Display minutes and seconds
    };

    const totalTimeSpentChecked = !row.statistics?.totalTimeSpent
      ? 0
      : row.statistics?.totalTimeSpent > myClass.duration * 60
      ? myClass.duration * 60
      : row.statistics?.totalTimeSpent;
    return (
      <>
        <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
          <TableCell sx={{ padding: "16px 0" }}>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => {
                setOpen(!open);
              }}
              sx={{ padding: 0, width: "20px", height: "20px" }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell sx={{ padding: "16px 0" }}>
            <Avatar
              alt={`avatar-${row.firstname}-${row.lastname}`}
              src={row.account.avatarUrl}
              imgProps={{
                loading: "eager",
                onLoad: (e) => {
                  e.target.style.opacity = 1;
                },
              }}
              sx={{
                "& .MuiAvatar-img": {
                  opacity: 0,
                  transition: "opacity 0.3s",
                },
                width: "30px",
                height: "30px",
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              maxWidth: width < 769 ? "80px" : "auto",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.lastname}
          </TableCell>
          <TableCell
            sx={{
              maxWidth: width < 769 ? "80px" : "auto",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.firstname}
          </TableCell>
          <TableCell
            sx={{
              padding: width < 769 ? "8px 0" : "16px",
            }}
            align="center"
          >
            <Tooltip
              title={`${t("classManagement.statistics.sendEmailTo")} ${
                row.account.email
              }`}
            >
              <IconButton
                color="primary"
                href={`mailto:${row.account.email}`}
                size="small"
              >
                <EmailIcon sx={{ width: width < 768 ? "20px" : "inherit" }} />
              </IconButton>
            </Tooltip>
          </TableCell>

          <TableCell align="center">
            {formatTimeSpentInSeconds(totalTimeSpentChecked)}
            <LinearProgressWithLabel
              variant="determinate"
              value={Math.min(
                (totalTimeSpentChecked / (myClass.duration * 60)) * 100,
                100
              )}
              displayMode={width < 769 ? "dot" : "bar"}
            />
          </TableCell>
          <TableCell
            sx={{
              padding: width < 769 ? "8px 0" : "16px",
            }}
            align="center"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Lottie
                animationData={PacesLottie}
                loop={false}
                style={{ width: "60px", height: "60px", marginTop: "-25px" }}
              />
              <Typography variant="body2" sx={{ marginTop: "-10px" }}>
                {Number.parseFloat(row.statistics?.totalPaces).toFixed(0) || 0}
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ padding: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  {t("classManagement.statistics.history")}
                </Typography>
                {(row.statistics?.details?.length ?? 0) === 0 ? (
                  <Table size="small" aria-label="details">
                    <TableBody>
                      {t("classManagement.statistics.historyEmpty")}
                    </TableBody>
                  </Table>
                ) : (
                  <Table size="small" aria-label="details">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t("classManagement.statistics.date")}
                        </TableCell>
                        <TableCell>
                          {t("classManagement.statistics.timeSpent")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.statistics?.details
                        ?.filter((d) => d.timeSpent && d.timeSpent > 0)
                        .sort(
                          (a, b) =>
                            new Date(b.createdDate) - new Date(a.createdDate)
                        )
                        .map((historyRow, index) => (
                          <TableRow
                            key={`${row.statistics?.userId}-${
                              row.statistics?.groupId
                            }-${historyRow.createdDate}-${index + 1}`}
                          >
                            <TableCell component="th" scope="row">
                              {formatDate(historyRow.createdDate)}
                            </TableCell>
                            <TableCell>
                              {formatTimeSpent(historyRow.timeSpent)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary renders
    return (
      prevProps.row.id === nextProps.row.id &&
      prevProps.row.firstname === nextProps.row.firstname &&
      prevProps.row.lastname === nextProps.row.lastname &&
      prevProps.row.statistics?.totalTimeSpent ===
        nextProps.row.statistics?.totalTimeSpent &&
      // Only compare details length, not the entire details array
      (prevProps.row.statistics?.details?.length ?? 0) ===
        (nextProps.row.statistics?.details?.length ?? 0)
    );
  }
);
